/**
 * Bridges hidden/visible Yotpo Loyalty widgets into our own markup.
 *
 * Yotpo Loyalty doesn't expose a documented client-side API for a shopper's
 * raw tier/points data, so this observes the DOM Yotpo renders into its own
 * widget instances and copies text out once populated.
 *
 * Tier/progress selectors were confirmed by capturing the actual rendered
 * markup via devtools on a real logged-in session (instance ID 632926 on
 * store/au-live renders a "VIP Tiers" widget, not the "My Rewards"
 * headline/description widget the old rewards.liquid's CSS overrides
 * suggested - the class names below are what Yotpo actually outputs):
 *   .yotpo-progress-bar-top-right                        -> the TARGET tier name in the "YOU ... Level Y" row
 *                                                            (e.g. "Level 4") - NOT the shopper's current tier,
 *                                                            despite the name; confirmed live (2026-08-19) on a
 *                                                            real Level 3 account where this read "Level 4".
 *   .yotpo-vip-tiers-progress-bar-summary-current
 *     .yotpo-templated-variable                          -> the shopper's actual CURRENT tier (e.g. "Level 3"),
 *                                                            nested inside "You're a Level 3 member!" - scope the
 *                                                            selector to summary-current specifically, since the
 *                                                            sibling summary-next div also has its own
 *                                                            .yotpo-templated-variable spans (amount + next tier).
 *   .yotpo-vip-tiers-progress-bar-summary-next  -> "Spend $X to unlock Level Y" copy
 *   .yotpo-progress-bar-inner[data-percentage]  -> progress bar fill, e.g. data-percentage="42%"
 *
 * Ticket count lives in a completely different widget - the redemption one
 * embedded (visibly) in the Rewards Redeem section, instance ID 621360 on
 * store/au-live. Confirmed the same way via captured markup:
 *   .yotpo-points-balance-number -> the ticket count itself (e.g. "-54")
 * Since it's a separate section's widget, this searches the whole document
 * for it rather than duplicating the embed inside Activity.
 *
 * initTierProgress (above) depends on a .visually-hidden wrapper this
 * section no longer renders, so it's effectively inert now - kept as-is
 * since it's harmless and out of scope to remove here (it also targets
 * .yotpo-progress-bar-top-right for tier name, which per the note above is
 * actually wrong too - not worth fixing in dead code). initTierName reads
 * .yotpo-vip-tiers-progress-bar-summary-current's nested
 * .yotpo-templated-variable directly from the VIP Tiers widget's live,
 * visible markup inside this section's own panel instead, to show the
 * shopper's actual current tier alongside the ticket count.
 */
(function () {
  // How long to keep watching for Yotpo to populate before giving up. This
  // page loads at least two Yotpo widgets concurrently (tiers + redemption)
  // alongside other third-party scripts (Nosto, etc.), so 15s proved too
  // short in practice - the redemption widget in particular can take longer
  // than that to finish rendering.
  var GIVE_UP_AFTER_MS = 45000;

  function initTierProgress(root) {
    // Yotpo doesn't inject content INSIDE our <div class="yotpo-widget-instance">
    // - it fully replaces that element with its own markup (confirmed via
    // captured HTML: the class/attribute we set are gone entirely once Yotpo
    // takes over). An observer watching that element's own children would
    // never see that replacement, since it happens one level up, at its
    // parent. So this watches the stable .visually-hidden wrapper we control
    // instead (which Yotpo never touches) and searches inside it fresh each
    // time, regardless of how Yotpo restructures its own markup.
    var yotpoWrapper = root.parentElement.querySelector('.visually-hidden');
    if (!yotpoWrapper) return;

    var tierNameEl = root.querySelector('[data-rewards-tier-name]');
    var progressLabelEl = root.querySelector('[data-rewards-progress-label]');
    var progressFillEl = root.querySelector('[data-rewards-progress-fill]');

    function readWidget() {
      var tierName = yotpoWrapper.querySelector('.yotpo-progress-bar-top-right');
      var progressSummary = yotpoWrapper.querySelector('.yotpo-vip-tiers-progress-bar-summary-next');
      var progressFill = yotpoWrapper.querySelector('.yotpo-progress-bar-inner');

      if (!tierName && !progressSummary && !progressFill) return false;

      if (tierName && tierNameEl) tierNameEl.textContent = tierName.textContent.trim();
      if (progressSummary && progressLabelEl) progressLabelEl.textContent = progressSummary.textContent.trim();
      if (progressFill && progressFillEl) {
        var percentage = progressFill.dataset.percentage || progressFill.style.width || '0%';
        progressFillEl.style.width = percentage;
      }

      root.classList.remove('rewards-activity__panel--loading');
      return true;
    }

    if (readWidget()) return;

    var observer = new MutationObserver(function () {
      if (readWidget()) observer.disconnect();
    });
    observer.observe(yotpoWrapper, { childList: true, subtree: true });

    // Yotpo widgets can take a while to load; give up gracefully rather than
    // leaving the panel stuck in a loading state forever.
    window.setTimeout(function () {
      observer.disconnect();
    }, GIVE_UP_AFTER_MS);
  }

  function initTicketCount(root) {
    var ticketsRowEl = root.querySelector('.rewards-activity__ticket-count');
    var ticketCountEl = root.querySelector('[data-rewards-ticket-count]');
    if (!ticketsRowEl || !ticketCountEl) return;

    // Hidden until we actually find a real count - never leave it visibly blank.
    ticketsRowEl.style.display = 'none';

    function readTicketCount() {
      var ticketNumber = document.querySelector('.yotpo-points-balance-number');
      if (!ticketNumber) return false;

      // Guard against writing a value that's already in place. The observer
      // below watches document.body, which includes our OWN output elements
      // (they live on the same page) - writing unconditionally on every
      // mutation would make our own write count as a new mutation, re-firing
      // the observer, writing again, re-firing again... an infinite loop.
      // Only writing on an actual change breaks that cycle immediately.
      var newText = ticketNumber.textContent.trim();
      if (ticketCountEl.textContent !== newText) {
        ticketCountEl.textContent = newText;
      }
      if (ticketsRowEl.style.display !== '') {
        ticketsRowEl.style.display = '';
      }
      return true;
    }

    // Read whatever's there immediately (in case it's already loaded), then
    // keep syncing on every subsequent change - deliberately NOT disconnecting
    // after the first successful read. Yotpo renders a placeholder value
    // (e.g. "0") the instant the widget shell appears, then patches in the
    // real balance once its own API call resolves a moment later; stopping
    // after the first match means catching that placeholder and never
    // noticing the real value replace it. Continuously re-reading avoids
    // that regardless of how many times Yotpo updates the number.
    readTicketCount();

    // The redemption widget lives in the Rewards Redeem section's DOM, not
    // ours - observe its known container if present, falling back to the
    // whole body only if that section isn't on this page for some reason.
    // Deliberately no give-up timeout: in testing the redemption widget
    // reliably took longer than even a generous 45s window to settle, and
    // since this never disconnects on its own there's no meaningful cost to
    // just letting it keep watching for the life of the page.
    //
    // Watches .rewards-redeem (the section's own wrapper), NOT
    // .rewards-redeem__widget itself: confirmed live that Yotpo fully
    // replaces that div with its own markup (same behavior already found
    // and worked around for the tier/progress bridge above) rather than
    // injecting content inside it, so an observer attached directly to it
    // stops seeing anything the moment Yotpo swaps it out - the balance
    // never arrives. The wrapper one level up isn't touched by Yotpo and
    // stays a valid mutation target for the section's whole lifetime.
    var observeTarget = document.querySelector('.rewards-redeem') || document.body;
    var observer = new MutationObserver(function () {
      readTicketCount();
    });
    // childList alone only catches whole elements being added/removed. Yotpo
    // widgets commonly patch in personalized numbers as a plain text update
    // on an already-existing node - that's a characterData mutation, not a
    // childList one, so without this the observer would silently never
    // notice the count changing.
    observer.observe(observeTarget, { childList: true, subtree: true, characterData: true, attributes: true });
  }

  function initTierName(root) {
    var tierNameEl = root.querySelector('[data-rewards-tier-name]');
    if (!tierNameEl) return;

    function readTierName() {
      // Scoped to root (.rewards-activity__panel), not the whole document -
      // this widget lives inside our own panel already, unlike the
      // redemption widget's balance in initTicketCount above.
      //
      // .yotpo-vip-tiers-progress-bar-summary-current, NOT
      // .yotpo-progress-bar-top-right: confirmed live (2026-08-19) on a real
      // Level 3 account that top-right actually holds the TARGET tier
      // ("Level 4", matching the "Spend $X to unlock Level 4" copy), not
      // the shopper's current one. summary-current is the "You're a Level 3
      // member!" line - its nested .yotpo-templated-variable span holds
      // just "Level 3". Must scope to summary-current specifically: the
      // sibling summary-next div also has its own .yotpo-templated-variable
      // spans (the dollar amount AND the target tier), so an unscoped
      // document/root-wide query would risk matching the wrong one.
      var tierName = root.querySelector('.yotpo-vip-tiers-progress-bar-summary-current .yotpo-templated-variable');
      if (!tierName) return false;

      var newText = tierName.textContent.trim();
      if (tierNameEl.textContent !== newText) {
        tierNameEl.textContent = newText;
      }
      return true;
    }

    readTierName();

    // root is our own div, never replaced by Yotpo (only its children are) -
    // safe to observe directly, unlike .rewards-redeem__widget above.
    var observer = new MutationObserver(function () {
      readTierName();
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  }

  function initRewardsActivity(root) {
    initTierProgress(root);
    initTicketCount(root);
    initTierName(root);
  }

  document.addEventListener('DOMContentLoaded', function () {
    var panel = document.querySelector('[data-rewards-activity]');
    if (panel) initRewardsActivity(panel);
  });

  // The theme editor re-renders a section's HTML via AJAX whenever a setting
  // changes (e.g. pasting in the Yotpo instance ID) instead of reloading the
  // page, so DOMContentLoaded never fires again for that new markup. Without
  // this, the panel stays stuck in its initial "loading" state until a full
  // page refresh, even once the setting is correct.
  document.addEventListener('shopify:section:load', function (event) {
    var panel = event.target.querySelector('[data-rewards-activity]');
    if (panel) initRewardsActivity(panel);
  });
})();
